import { useState } from 'react';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';

import { U21Button, U21SplitButton } from 'app/shared/u21-ui/components';
import { IconBookmark } from '@u21/tabler-icons';

import { Filter, FilterContext, SavedFilter } from 'app/modules/filters/models';
import { EditSavedFilterModal } from 'app/modules/filters/components/EditSavedFilterModal';
import { SaveFilterModal } from 'app/modules/filters/components/SaveFilterModal';
import { selectAgent } from 'app/modules/session/selectors';
import { DeleteSavedFilterModal } from 'app/modules/filters/components/DeleteSavedFilterModal';

interface Props {
  appliedSavedFilter?: SavedFilter;
  defaultFilters: Filter[];
  duplicateSavedFilter?: SavedFilter;
  filterContext: FilterContext;
  filters: Filter[];
  onAppliedSavedFilterChange: (savedFilter: SavedFilter | undefined) => void;
}

export const SaveFilterButton = ({
  appliedSavedFilter,
  defaultFilters,
  duplicateSavedFilter,
  filterContext,
  filters,
  onAppliedSavedFilterChange,
}: Props) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const agent = useSelector(selectAgent);

  if (filters.length === 0 || isEqual(filters, defaultFilters)) {
    return null;
  }

  const isOwner =
    appliedSavedFilter && appliedSavedFilter.created_by_id === agent.id;
  const saveAsNewDisabled = duplicateSavedFilter !== undefined;
  const saveAsNewTooltip = duplicateSavedFilter
    ? `A saved filter with these filters already exists: "${duplicateSavedFilter.title}".`
    : undefined;

  return (
    <>
      {(() => {
        if (appliedSavedFilter && isOwner) {
          return (
            <U21SplitButton
              alignRight
              color="primary"
              disableTitleCase
              onClick={() => setEditModalOpen(true)}
              size="small"
              startIcon={<IconBookmark />}
              variant="outlined"
              items={[
                {
                  disabled: saveAsNewDisabled,
                  text: 'Save as new filter',
                  tooltip: saveAsNewTooltip,
                  onClick: () => setCreateModalOpen(true),
                },
                {
                  color: 'error',
                  text: 'Delete saved filter',
                  onClick: () => setDeleteModalOpen(true),
                },
              ]}
            >
              Update {appliedSavedFilter.title}
            </U21SplitButton>
          );
        }
        return (
          <U21Button
            color="primary"
            disabled={saveAsNewDisabled}
            onClick={() => setCreateModalOpen(true)}
            size="small"
            startIcon={<IconBookmark />}
            tooltip={saveAsNewTooltip}
            variant="outlined"
          >
            {appliedSavedFilter ? 'Save as new filter' : 'Save Filter'}
          </U21Button>
        );
      })()}
      <SaveFilterModal
        filterContext={filterContext}
        filters={filters}
        onClose={() => setCreateModalOpen(false)}
        onSaved={onAppliedSavedFilterChange}
        open={createModalOpen}
      />
      {appliedSavedFilter && isOwner && (
        <>
          <EditSavedFilterModal
            filters={filters}
            onClose={() => setEditModalOpen(false)}
            onUpdated={onAppliedSavedFilterChange}
            open={editModalOpen}
            savedFilter={appliedSavedFilter}
          />
          <DeleteSavedFilterModal
            filterContext={filterContext}
            onClose={() => setDeleteModalOpen(false)}
            onDeleted={() => onAppliedSavedFilterChange(undefined)}
            open={deleteModalOpen}
            savedFilter={appliedSavedFilter}
          />
        </>
      )}
    </>
  );
};
